import { SanityDocument } from 'sanity'
import { createClient } from '@sanity/client'

interface DocumentWithPageInfo extends SanityDocument {
  pageInfo?: {
    canonical?: {
      current?: string
    }
  }
  design?: {
    _ref?: string
  }
  accessoryPage?: {
    _ref?: string
  }
}

/**
 * Wrap a target site path in the frontend's /preview/enable redirect, which
 * sets the sanity-preview cookie (driving the client-side draft data path) and
 * redirects to the page. It deliberately does NOT set Vercel's
 * __prerender_bypass cookie; see frontend/server/routes/preview/enable.get.ts.
 *
 * `path` is the site-relative destination (e.g. "/blog/my-post"); `params`
 * carries preview/dataset/release. Returns the full enable URL.
 */
function buildPreviewUrl(
  baseURL: string,
  path: string,
  params: URLSearchParams
): string {
  const enable = new URLSearchParams()
  enable.set('redirect', path)
  for (const [key, value] of params.entries()) enable.set(key, value)
  return `${baseURL}/preview/enable?${enable.toString()}`
}

export default async function resolveProductionUrl(
  document: DocumentWithPageInfo,
  context?: any
): Promise<string | undefined> {
  // Get base URL from environment variable with fallback
  const baseURL = process.env.SANITY_STUDIO_FE_URL || 'http://localhost:3000'

  const { _type: t = '', _id } = document
  const { current: slug } = document?.pageInfo?.canonical || {}

  // Create client for async queries (used by wheelLanding and sku)
  const createSanityClient = () => createClient({
    projectId: process.env.SANITY_STUDIO_PROJECT_ID || 'c8ihu5xk',
    dataset: context?.dataset || process.env.SANITY_STUDIO_DATASET || 'preview',
    apiVersion: '2025-02-19',
    useCdn: false,
  })

  // Special handling for SKU - need to fetch accessory page slug
  if (t === 'sku' && document.accessoryPage?._ref && slug) {
    try {
      const client = createSanityClient()
      const query = `*[_id == $accessoryPageId][0]{ 'categorySlug': pageInfo.canonical.current }`
      const result = await client.fetch(query, { accessoryPageId: document.accessoryPage._ref })

      if (result?.categorySlug) {
        const params = new URLSearchParams()
        params.set('preview', _id)
        if (context?.dataset) params.set('dataset', context.dataset)

        let releaseId: string | undefined = context?.selectedPerspectiveName
        if (releaseId && (releaseId === 'drafts' || releaseId === 'published')) {
          releaseId = undefined
        }
        if (releaseId) params.set('release', releaseId)

        return buildPreviewUrl(baseURL, `/accessories/${result.categorySlug}/${slug}`, params)
      }
    } catch (error) {
      console.error('[resolveProductionUrl] Error fetching SKU accessory page:', error)
    }
  }

  // Special handling for wheelLanding - need to fetch construction type and line slugs
  if (t === 'wheelLanding' && document.design?._ref) {
    try {
      const client = createSanityClient()
      const query = `*[_id == $designId][0]{
        ...line->{
          'lineSlug': pageInfo.canonical.current,
          'constructionType': constructionType->{
            'constructionTypeSlug': pageInfo.canonical.current,
          }
        }
      }`

      const result = await client.fetch(query, { designId: document.design._ref })

      if (result?.constructionType?.constructionTypeSlug && result?.lineSlug && slug) {
        const wheelPath = `${result.constructionType.constructionTypeSlug}/${result.lineSlug}/${slug}`

        // Build preview query params
        const params = new URLSearchParams()
        params.set('preview', _id)
        if (context?.dataset) params.set('dataset', context.dataset)

        let releaseId: string | undefined = context?.selectedPerspectiveName
        if (releaseId && (releaseId === 'drafts' || releaseId === 'published')) {
          releaseId = undefined
        }
        if (releaseId) params.set('release', releaseId)

        return buildPreviewUrl(baseURL, `/wheels/${wheelPath}`, params)
      }
    } catch (error) {
      console.error('[resolveProductionUrl] Error fetching wheel path:', error)
    }
  }

  // Special handling for buyerGuide + fitmentGuide - both live at
  // /fitment-guides/{make}/{model}/{slug} (NOT /fitment-guides/{slug}), reached
  // via the generation that references them (generation.buyerGuide /
  // generation.fitmentGuide — the ref field name equals the document _type).
  if ((t === 'buyerGuide' || t === 'fitmentGuide') && slug) {
    try {
      const client = createSanityClient()
      // Find the generation that references this guide, then the model that
      // contains that generation, then the make that contains that model.
      const publishedId = _id.replace('drafts.', '')
      const query = `*[_type == 'generation' && ${t}._ref == $docId][0]{
        'modelData': *[_type == 'model' && references(^._id)][0]{
          'modelSlug': pageInfo.canonical.current,
          'makeData': *[_type == 'make' && references(^._id)][0]{
            'makeSlug': pageInfo.canonical.current
          }
        }
      }`

      const result = await client.fetch(query, { docId: publishedId })

      const makeSlug = result?.modelData?.makeData?.makeSlug
      const modelSlug = result?.modelData?.modelSlug

      if (makeSlug && modelSlug) {
        const params = new URLSearchParams()
        params.set('preview', _id)
        if (context?.dataset) params.set('dataset', context.dataset)

        let releaseId: string | undefined = context?.selectedPerspectiveName
        if (releaseId && (releaseId === 'drafts' || releaseId === 'published')) {
          releaseId = undefined
        }
        if (releaseId) params.set('release', releaseId)

        return buildPreviewUrl(baseURL, `/fitment-guides/${makeSlug}/${modelSlug}/${slug}`, params)
      }
    } catch (error) {
      console.error('[resolveProductionUrl] Error fetching fitment/buyer guide path:', error)
    }
  }

  // Special handling for article - blog articles live at /blog/{category}/{slug},
  // not /blog/{slug}. Fetch the first category's slug (matching the front end's
  // firstCategory = categories[0] used to build the canonical URL).
  if (t === 'article' && slug) {
    try {
      const client = createSanityClient()
      const publishedId = _id.replace('drafts.', '')
      const result = await client.fetch(
        `*[_id == $docId][0]{ 'categorySlug': categories[0]->pageInfo.canonical.current }`,
        { docId: publishedId },
      )
      const categorySlug = result?.categorySlug

      const params = new URLSearchParams()
      params.set('preview', _id)
      if (context?.dataset) params.set('dataset', context.dataset)

      let releaseId: string | undefined = context?.selectedPerspectiveName
      if (releaseId && (releaseId === 'drafts' || releaseId === 'published')) {
        releaseId = undefined
      }
      if (releaseId) params.set('release', releaseId)

      const path = categorySlug ? `/blog/${categorySlug}/${slug}` : `/blog/${slug}`
      return buildPreviewUrl(baseURL, path, params)
    } catch (error) {
      console.error('[resolveProductionUrl] Error fetching article category:', error)
    }
  }

  // Extract release ID from context (passed by Sanity when viewing a release)
  let releaseId: string | undefined = context?.selectedPerspectiveName

  // Filter out non-release perspectives (drafts, published, etc.)
  if (releaseId && (releaseId === 'drafts' || releaseId === 'published')) {
    releaseId = undefined
  }

  // Build preview query params
  const params = new URLSearchParams()
  params.set('preview', _id)

  if (context?.dataset) {
    params.set('dataset', context.dataset)
  }

  if (releaseId) {
    params.set('release', releaseId)
  }

  // Site-relative destination paths per document type (wrapped in
  // /preview/enable below so the iframe lands in preview mode).
  const paths: Record<string, string> = {
    home: `/`,
    accessories: `/accessories`,
    accessory: `/accessories/${slug}`,
    bestSellersPage: `/best-sellers`,
    contact: `/contact-us`,
    wheelLines: `/wheels`,
    gallery: `/gallery`,
    cart: `/cart`,
    promotions: `/sale`,
    // Additional document types from v4 schemas
    // (fitmentGuide + article are handled by the special cases above — they need
    // make/model and category segments respectively, not a bare slug.)
    articleCategory: `/blog/${slug}`,
    customerSupport: `/customer-support`,
    customerSupportArticle: `/customer-support/${slug}`,
    dynamicPages: `/${slug}`,
    // Single pages
    vehicles: `/vehicles`,
    sprintLine: `/wheels/forged/sprint-line`,
    comingSoon: `/coming-soon`,
    taxRefund: `/taxrefund`,
    blackFriday: `/blackfriday`,
    memorialDay: `/memorialday`,
    project: `/projectml10`,
    realPerformancePage: `/realperformance`,
    fiftyReplacement: `/replacement-program`,
    replacementEligibility: `/replacement-program/eligibility`,
    replacementWheel: `/replacement-program/choose-replacement-wheel`,
    resourceCenter: `/resources`,
    resourceCenterArticle: `/resources/${slug}`,
  }

  // Return preview URL routed through /preview/enable (sets the sanity-preview cookie)
  if (paths[t]) return buildPreviewUrl(baseURL, paths[t], params)

  return undefined
}
